import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useBloqueoAutogestion } from "services/bloqueoAutogestion";
import styled from "@emotion/styled";
import {
  Alert,
  Card,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";
import Cafeteria from "../Cafeteria";
import MenuList from "../MenuList";
import ModalConfirmarPedido from "../ModalConfirmarPedido";
import { UserContext } from "context/user";
import { formatMoneyPunto } from "utils/formatters";
import { LIMITE_CUENTA_CORRIENTE_FALLBACK } from "utils/cuentaCorrienteConfig";
import {
  DESTINO_VM,
  DESTINOS_SEDE_2,
  requiereDestinoExplicito,
  resolveDestinoForSede,
} from "../pedidoDestino";
import { isVoucherCalificanteCafeteria } from "../../utils/voucherCafeteria";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  overflowY: "auto",
  p: 3,
  transition: "width 0.5s",
};

const normalizeTime = (value = "") => {
  const time = String(value || "").trim();
  if (!time) return null;
  if (/^\d{2}:\d{2}$/.test(time)) return `${time}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
  return null;
};

const normalizeOffset = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : fallback;
};

const toDateTimeWithOffset = (baseDate, timeValue, offsetDays = 0) => {
  const normalizedTime = normalizeTime(timeValue);
  if (!normalizedTime) return null;
  const [hour, minute, second] = normalizedTime.split(":").map(Number);
  return dayjs(baseDate)
    .add(Number(offsetDays || 0), "day")
    .hour(hour)
    .minute(minute)
    .second(second)
    .millisecond(0);
};

const isPedidoWindowConfigured = (turno = {}) =>
  Boolean(normalizeTime(turno?.pedidoDesdeHora) && normalizeTime(turno?.pedidoHastaHora));

const isTurnoPedidoWindowActiveNow = (turno = {}, now = dayjs()) => {
  if (!isPedidoWindowConfigured(turno)) return true;
  const desdeOffset = normalizeOffset(turno?.pedidoDesdeOffsetDias, 0);
  const hastaOffset = normalizeOffset(turno?.pedidoHastaOffsetDias, 0);
  const baseToday = now.startOf("day");
  const baseTomorrow = baseToday.add(1, "day");
  const candidatosBase = [baseToday, baseTomorrow];

  return candidatosBase.some((baseDate) => {
    let desde = toDateTimeWithOffset(baseDate, turno?.pedidoDesdeHora, desdeOffset);
    let hasta = toDateTimeWithOffset(baseDate, turno?.pedidoHastaHora, hastaOffset);
    if (!desde || !hasta || !desde.isValid() || !hasta.isValid()) return false;
    if (!hasta.isAfter(desde)) {
      hasta = hasta.add(1, "day");
    }
    return (now.isAfter(desde) || now.isSame(desde)) && now.isBefore(hasta);
  });
};

const doesTurnoPedidoWindowCrossMidnight = (turno = {}) => {
  if (!isPedidoWindowConfigured(turno)) return false;
  const desdeOffset = normalizeOffset(turno?.pedidoDesdeOffsetDias, 0);
  const hastaOffset = normalizeOffset(turno?.pedidoHastaOffsetDias, 0);
  const baseDate = dayjs("2026-01-01");
  let desde = toDateTimeWithOffset(baseDate, turno?.pedidoDesdeHora, desdeOffset);
  let hasta = toDateTimeWithOffset(baseDate, turno?.pedidoHastaHora, hastaOffset);
  if (!desde || !hasta || !desde.isValid() || !hasta.isValid()) return false;
  if (!hasta.isAfter(desde)) {
    hasta = hasta.add(1, "day");
  }
  return !desde.isSame(hasta, "day");
};

const resolveMenuDateForTurno = ({ turno, franjas = [], now = dayjs() }) => {
  const cruzaMedianoche = doesTurnoPedidoWindowCrossMidnight(turno);
  if (!cruzaMedianoche) {
    return {
      fechaMenu: now.startOf("day"),
      usaDiaSiguiente: false,
    };
  }

  const hoy = now.startOf("day");
  const franjasConRango = (franjas || [])
    .map((franja) => {
      const inicio = toDateTimeWithOffset(hoy, franja?.horaInicio, 0);
      let fin = toDateTimeWithOffset(hoy, franja?.horaFin, 0);
      if (!inicio || !fin || !inicio.isValid() || !fin.isValid()) return null;
      if (!fin.isAfter(inicio)) {
        fin = fin.add(1, "day");
      }
      return { inicio, fin };
    })
    .filter(Boolean);

  if (franjasConRango.length === 0) {
    return {
      fechaMenu: hoy,
      usaDiaSiguiente: false,
    };
  }

  const ahoraEnFranja = franjasConRango.some(
    ({ inicio, fin }) => (now.isAfter(inicio) || now.isSame(inicio)) && now.isBefore(fin)
  );
  if (ahoraEnFranja) {
    return {
      fechaMenu: hoy,
      usaDiaSiguiente: false,
    };
  }

  const ahoraMayorATodas = franjasConRango.every(({ fin }) => now.isAfter(fin) || now.isSame(fin));
  if (ahoraMayorATodas) {
    return {
      fechaMenu: hoy.add(1, "day"),
      usaDiaSiguiente: true,
    };
  }

  return {
    fechaMenu: hoy,
    usaDiaSiguiente: false,
  };
};

export default function ModalNuevoPedido({
  open,
  handleClose,
  refetch,
  onPedidoCreado,
  pedidosHoy,
  token,
  usuario,
  vouchersActivos = [],
  loadingVouchersActivos = false,
  onActualizarVouchers,
  deshabilitar = false,
  saldoCuentaCorrientePendienteActual = 0,
  limiteCuentaCorriente = LIMITE_CUENTA_CORRIENTE_FALLBACK,
  bloqueoCuentaCorrienteLimite = false,
}) {
  const [fechaPedido, setFechaPedido] = useState(dayjs());
  const [turnoPedido, setTurnoPedido] = useState("");
  const [horaReserva, setHoraReserva] = useState("");
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [franjaSeleccionada, setFranjaSeleccionada] = useState(null);
  const [turnosDisponibles, setTurnosDisponibles] = useState([]);
  const [franjasDisponibles, setFranjasDisponibles] = useState([]);
  const [configPedidosSede, setConfigPedidosSede] = useState({
    permitirPedidosDiaSiguiente: false,
    maxPedidosUsuarioPorDia: 2,
  });
  const [usaDiaSiguiente, setUsaDiaSiguiente] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState();
  const [animatedIcon, setAnimatedIcon] = useState("");
  const [step, setStep] = useState(0);
  const [paraRetirar, setParaRetirar] = useState(false);
  const [paraLlevar, setParaLlevar] = useState(false);
  const [openModalConfirmar, setOpenModalConfirmar] = useState(false);
  const [vouchersPedido, setVouchersPedido] = useState([]);
  const [voucherSeleccionado, setVoucherSeleccionado] = useState(null);
  const [idVoucherAccesoCafeteria, setIdVoucherAccesoCafeteria] = useState("");
  const [mostrarVouchers, setMostrarVouchers] = useState(false);
  const [vouchersExpandidos, setVouchersExpandidos] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [dondeLlevar, setDondeLlevar] = useState("");
  const [usuarioSincronizado, setUsuarioSincronizado] = useState(usuario);
  const [clienteSyncReady, setClienteSyncReady] = useState(false);
  const idSedeActual = localStorage.getItem("idSede") || "1";
  const { logout } = useContext(UserContext);

  const { confirmar } = useContext(ConfirmarContext);
  const [syncClienteVentaOnline, { loading: syncingCliente }] = useMutation(
    gql`
      mutation syncClienteVentaOnline($usuario: UsuarioVentaOnlineInput!, $token: String) {
        syncClienteVentaOnline(usuario: $usuario, token: $token) {
          id
          nombre
          apellido
          dni
          correo
          appUsuario
          tipoUsuario
          cuentaUpro
          habilitadoCuentaCorriente
          esBecado
          usoVoucher
          fechaVtoVoucher
        }
      }
    `
  );

  const [getMenusDelDia, { data: menusDelDia, error: menusDelDiaError }] = useLazyQuery(
    gql`
      query getMenusDelDia($filter: JSON, $token: String, $idSede: ID) {
        menusDelDia(filter: $filter, token: $token, idSede: $idSede) {
          id
          precio
          fechaMenu
          limiteReservas
          cantidadReservas
          producto {
            id
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
      errorPolicy: "all",
    }
  );
  const [getTurnosSede] = useLazyQuery(
    gql`
      query getTurnosSede($idSede: ID!, $token: String) {
        turnosSede(idSede: $idSede, token: $token) {
          id
          nombre
          horaInicio
          horaFin
          pedidoDesdeHora
          pedidoDesdeOffsetDias
          pedidoHastaHora
          pedidoHastaOffsetDias
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getFranjasTurno] = useLazyQuery(
    gql`
      query getFranjasTurno($idTurnoSede: ID!, $idSede: ID, $fecha: String, $token: String) {
        franjasTurno(idTurnoSede: $idTurnoSede, idSede: $idSede, fecha: $fecha, token: $token) {
          id
          idTurnoSede
          turno
          horaInicio
          horaFin
          cantidadReservas
          disponible
          vencida
          sinCupo
          fueraVentanaPedidos
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getConfigPedidosSede] = useLazyQuery(
    gql`
      query getConfigPedidosSede($idSede: ID!, $token: String) {
        configPedidosSede(idSede: $idSede, token: $token) {
          idSede
          permitirPedidosDiaSiguiente
          maxPedidosUsuarioPorDia
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [previsualizarVouchersPedido, { loading: loadingVouchersPedido }] = useLazyQuery(
    PREVISUALIZAR_VOUCHERS_PEDIDO,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (!open) {
      setClienteSyncReady(false);
      return;
    }

    if (!token || !usuario?.id) {
      setUsuarioSincronizado(usuario);
      setClienteSyncReady(true);
      return;
    }

    let isMounted = true;
    setClienteSyncReady(false);
    setUsuarioSincronizado(usuario);

    syncClienteVentaOnline({
      variables: {
        token,
        usuario: {
          id: usuario?.id,
          nombre: usuario?.nombre,
          apellido: usuario?.apellido,
          dni: usuario?.dni,
          username: usuario?.username,
          email: usuario?.email,
          tipoUser: usuario?.tipoUser,
          sistema: usuario?.sistema,
          cliente: usuario?.cliente,
          cuentaUpro: usuario?.cuentaUpro,
          habilitadoCuentaCorriente: usuario?.habilitadoCuentaCorriente,
          clienteHabilitado: usuario?.clienteHabilitado,
          esBecado: usuario?.esBecado,
        },
      },
    })
      .then((res) => {
        if (!isMounted) return;
        const cliente = res?.data?.syncClienteVentaOnline;
        setUsuarioSincronizado((prev) => ({
          ...(prev || usuario || {}),
          nombre: cliente?.nombre ?? prev?.nombre,
          apellido: cliente?.apellido ?? prev?.apellido,
          dni: cliente?.dni ?? prev?.dni,
          email: cliente?.correo ?? prev?.email,
          cuentaUpro: cliente?.cuentaUpro ?? prev?.cuentaUpro,
          habilitadoCuentaCorriente:
            cliente?.habilitadoCuentaCorriente ?? prev?.habilitadoCuentaCorriente,
          esBecado: cliente?.esBecado ?? prev?.esBecado,
          usoVoucher: cliente?.usoVoucher ?? prev?.usoVoucher,
          fechaVtoVoucher: cliente?.fechaVtoVoucher ?? prev?.fechaVtoVoucher,
        }));
        setClienteSyncReady(true);
      })
      .catch((error) => {
        if (!isMounted) return;

        const message =
          error?.graphQLErrors?.[0]?.message ||
          error?.message ||
          "No fue posible validar tu cuenta para realizar pedidos.";

        if (typeof handleClose === "function") {
          handleClose();
        }

        confirmar({
          icon: "error",
          title: "Operacion no disponible",
          message,
          pedirMotivo: false,
          func: () => {
            const origen = new URLSearchParams(window.location.search).get("origin");
            if (origen) {
              logout();
              window.location.href = origen;
            } else {
              logout();
              window.location.href = `${window.location.origin}/login`;
            }
          },
        });
      });

    return () => {
      isMounted = false;
    };
  }, [open, token, usuario?.id]);

  useEffect(() => {
    if (!open || !token || !clienteSyncReady) return;
    getConfigPedidosSede({
      variables: {
        idSede: idSedeActual,
        token,
      },
    }).then((res) => {
      const config = res?.data?.configPedidosSede;
      if (!config) return;
      setConfigPedidosSede({
        permitirPedidosDiaSiguiente: Boolean(config.permitirPedidosDiaSiguiente),
        maxPedidosUsuarioPorDia: Number(config.maxPedidosUsuarioPorDia || 2),
      });
    });
  }, [open, token, clienteSyncReady, idSedeActual]);

  useEffect(() => {
    if (!open || !token || !clienteSyncReady || selectedMenu !== "menu") return;

    const now = dayjs();
    getTurnosSede({
      variables: {
        idSede: idSedeActual,
        token,
      },
    }).then((res) => {
      const turnos = (res?.data?.turnosSede || []).filter((turno) =>
        isTurnoPedidoWindowActiveNow(turno, now)
      );

      setTurnosDisponibles(turnos);

      if (turnos.length === 0 || !turnos.some((turno) => String(turno.id) === String(turnoPedido))) {
        setTurnoPedido("");
        setTurnoSeleccionado(null);
        setHoraReserva("");
        setFranjaSeleccionada(null);
        setFranjasDisponibles([]);
        setFechaPedido(dayjs());
        setUsaDiaSiguiente(false);
      } else {
        const turnoActualizado = turnos.find(
          (turno) => String(turno.id) === String(turnoPedido)
        );
        setTurnoSeleccionado(turnoActualizado || null);
      }
    });
  }, [
    open,
    token,
    clienteSyncReady,
    selectedMenu,
    idSedeActual,
    turnoPedido,
  ]);

  useEffect(() => {
    if (!open || !token || !clienteSyncReady || !turnoPedido || selectedMenu !== "menu") return;
    const turnoActual = turnosDisponibles.find(
      (item) => String(item.id) === String(turnoPedido)
    );
    if (!turnoActual) return;

    const now = dayjs();
    const hoyISO = now.format("YYYY-MM-DD");
    getFranjasTurno({
      variables: {
        idTurnoSede: turnoPedido,
        idSede: idSedeActual,
        fecha: hoyISO,
        token,
      },
    }).then(async (resHoy) => {
      const franjasHoy = resHoy?.data?.franjasTurno || [];
      const resolucionFecha = resolveMenuDateForTurno({
        turno: turnoActual,
        franjas: franjasHoy,
        now,
      });
      const fechaObjetivo = resolucionFecha?.fechaMenu || now.startOf("day");
      const fechaObjetivoISO = fechaObjetivo.format("YYYY-MM-DD");

      let franjasObjetivo = franjasHoy;
      if (fechaObjetivoISO !== hoyISO) {
        const resObjetivo = await getFranjasTurno({
          variables: {
            idTurnoSede: turnoPedido,
            idSede: idSedeActual,
            fecha: fechaObjetivoISO,
            token,
          },
        });
        franjasObjetivo = resObjetivo?.data?.franjasTurno || [];
      }

      setFechaPedido(fechaObjetivo);
      setUsaDiaSiguiente(Boolean(resolucionFecha?.usaDiaSiguiente));
      setFranjasDisponibles(franjasObjetivo);

      if (!franjasObjetivo.some((franja) => String(franja.id) === String(horaReserva))) {
        setHoraReserva("");
        setFranjaSeleccionada(null);
      } else {
        const franjaActualizada = franjasObjetivo.find(
          (franja) => String(franja.id) === String(horaReserva)
        );
        setFranjaSeleccionada(franjaActualizada || null);
      }
    });
  }, [open, token, clienteSyncReady, selectedMenu, turnoPedido, idSedeActual, turnosDisponibles]);

  const turnoSeleccionadoNombre =
    turnoSeleccionado?.nombre ||
    turnosDisponibles.find((turno) => String(turno.id) === String(turnoPedido))?.nombre ||
    "";

  useEffect(() => {
    if (
      !open ||
      !token ||
      !clienteSyncReady ||
      selectedMenu !== "menu" ||
      !turnoSeleccionadoNombre ||
      !franjaSeleccionada
    ) {
      return;
    }
    const fechaISO = dayjs(fechaPedido).format("YYYY-MM-DD");
    getMenusDelDia({
      variables: {
        idSede: idSedeActual,
        filter: {
          turno: turnoSeleccionadoNombre,
          fechaPedido: fechaISO,
        },
        token,
      },
    });
  }, [
    open,
    token,
    clienteSyncReady,
    selectedMenu,
    turnoSeleccionadoNombre,
    idSedeActual,
    fechaPedido,
    franjaSeleccionada,
  ]);

  useEffect(() => {
    if (selectedMenu === "menu") return;
    setTurnoPedido("");
    setTurnoSeleccionado(null);
    setHoraReserva("");
    setFranjaSeleccionada(null);
    setUsaDiaSiguiente(false);
  }, [selectedMenu]);

  useEffect(() => {
    if (!open) {
      setCarrito([]);
      setHoraReserva("");
      setParaRetirar(false);
      setUsuarioSincronizado(usuario);
    }
  }, [open]);

  useEffect(() => {
    if (!menusDelDiaError) return;

    const message =
      menusDelDiaError?.graphQLErrors?.[0]?.message ||
      menusDelDiaError?.message ||
      "No fue posible obtener los menus del dia.";

    if (typeof handleClose === "function") {
      handleClose();
    }

    confirmar({
      icon: "error",
      title: "Operacion no disponible",
      message,
      pedirMotivo: false,
      func: () => window.location.reload(),
    });
  }, [menusDelDiaError, confirmar, handleClose]);

  const AnimatedIcon = styled(Icon)(({ animate }) => ({
    transition: "transform 0.3s",
    animation: animate ? "shakes 1s" : "none",
    "@keyframes shakes": {
      "0%": { transform: "rotate(0deg)" },
      "25%": { transform: "rotate(10deg)" },
      "50%": { transform: "rotate(-20deg)" },
      "75%": { transform: "rotate(15deg)" },
      "100%": { transform: "rotate(-5deg)" },
    },
  }));

  const handleClick = (menuName) => {
    setAnimatedIcon(menuName);
    if (selectedMenu === menuName) {
      setSelectedMenu();
    } else {
      setSelectedMenu(menuName);
    }

    setTimeout(() => setAnimatedIcon(""), 1000);
  };

  useEffect(() => {
    setCarrito([]);
    setTurnoPedido("");
    setTurnoSeleccionado(null);
    setHoraReserva("");
    setFranjaSeleccionada(null);
    setTurnosDisponibles([]);
    setFranjasDisponibles([]);
    setSelectedMenu("");
    setParaRetirar(false);
    setParaLlevar(false);
    setDondeLlevar("");
    setStep(0);
    setAnimatedIcon("");
    setOpenModalConfirmar(false);
    setVouchersPedido([]);
    setVoucherSeleccionado(null);
    setIdVoucherAccesoCafeteria("");
    setMostrarVouchers(false);
    setVouchersExpandidos([]);
    setSelectedMenu("");
    setFechaPedido(dayjs());
    setUsaDiaSiguiente(false);
  }, [open]);

  const usuarioActual = usuarioSincronizado || usuario;
	useEffect(() => {
	  setVouchersPedido([]);
	  setVoucherSeleccionado(null);
	  setMostrarVouchers(false);
	  setVouchersExpandidos([]);
	}, [
	  carrito,
	  fechaPedido,
	  idSedeActual,
	  usuarioActual?.cliente,
	  usuarioActual?.dni,
	]);
  const todosVouchersExpandidos =
    vouchersPedido.length > 0 && vouchersPedido.every((voucher) => vouchersExpandidos.includes(voucher.id));

  const toggleVoucherExpandido = (idVoucher) => {
    setVouchersExpandidos((actuales) =>
      actuales.includes(idVoucher)
        ? actuales.filter((id) => id !== idVoucher)
        : [...actuales, idVoucher]
    );
  };

  const toggleTodosVouchers = () => {
    setVouchersExpandidos(
      todosVouchersExpandidos ? [] : vouchersPedido.map((voucher) => voucher.id)
    );
  };

  const handleFinalizarPedido = async () => {
    const productosActivos = (carrito || []).filter((item) => !item.eliminado);
    const totalPedido = productosActivos.reduce(
      (acc, item) => acc + Number(item.precioCarta || 0) * Number(item.cantidad || 0),
      0
    );
    const detalles = productosActivos.map((producto) => ({
      idProducto: producto?.id,
      cantidad: producto?.cantidad,
      precio: producto?.precioCarta,
      idMenuDelDia: menusDelDia?.menusDelDia?.find(
        (menu) => Number(menu?.producto?.id) === Number(producto?.id)
      )?.id,
      esElaborado: producto?.esElaborado || false,
    }));

    try {
      const response = await previsualizarVouchersPedido({
        variables: {
          idCliente: Number(usuarioActual?.cliente || 0) || null,
          dni: String(usuarioActual?.dni || ""),
          idUsuario: Number(usuarioActual?.id || 0) || null,
          appUsuario: usuarioActual?.sistema || usuarioActual?.appUsuario || null,
          fechaOperacion: dayjs(fechaPedido).format("YYYY-MM-DD"),
          idSede: Number(idSedeActual),
          totalPedido,
          detalles,
          token,
        },
      });
      const vouchers = response?.data?.previsualizarVouchersPedido || [];

      setVouchersPedido(vouchers);
      setVoucherSeleccionado(null);
      setMostrarVouchers(false);
      setVouchersExpandidos([]);

      if (requiereVoucherCafeteria) {
        const voucherRevalidado = vouchers.find(
          (voucher) =>
            String(voucher.id) === String(idVoucherAccesoCafeteria) &&
            voucher.elegible &&
            isVoucherCalificanteCafeteria(voucher, {
              idSede: idSedeActual,
              fecha: fechaPedido,
            })
        );

        if (!voucherRevalidado) {
          try {
            await onActualizarVouchers?.();
          } catch (refreshError) {
            console.error("No se pudieron actualizar los vouchers activos:", refreshError);
          }
          const voucherNoAplicable = vouchers.find(
            (voucher) => String(voucher.id) === String(idVoucherAccesoCafeteria)
          );
          confirmar({
            icon: "warning",
            title: "El voucher ya no puede aplicarse",
            message:
              voucherNoAplicable?.motivoBloqueo ||
              "Revisá los productos y cantidades del carrito o seleccioná otro voucher.",
            pedirMotivo: false,
          });
          return;
        }

        setVoucherSeleccionado(voucherRevalidado);
        setOpenModalConfirmar(true);
        return;
      }

      if (vouchers.length > 0) {
        setStep(3);
      } else {
        setOpenModalConfirmar(true);
      }
    } catch (previewError) {
      const message =
        previewError?.graphQLErrors?.[0]?.message ||
        previewError?.message ||
        "No fue posible consultar los vouchers y beneficios disponibles.";

      confirmar({
        icon: "error",
        title: "No pudimos validar los beneficios",
        message,
        pedirMotivo: false,
      });
    }
  };

  const limiteMaximoPedidos = Number(configPedidosSede?.maxPedidosUsuarioPorDia || 2);
  const esFechaHoy = dayjs(fechaPedido).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
  const pedidosActivosHoy =
    pedidosHoy?.filter(
      (pedido) =>
        !["Anulado", "ANULADO"].includes(pedido.estado) && pedido.turnoReserva != null
    ) || [];
  const limiteDiarioAlcanzado =
    esFechaHoy && pedidosActivosHoy.length >= limiteMaximoPedidos && !usuarioActual?.vip;

  const [selectedLocation, setSelectedLocation] = useState(() =>
    resolveDestinoForSede({
      idSede: localStorage.getItem("idSede") || "1",
      destino: localStorage.getItem("selectedLocation"),
    })
  );
  const esUsuarioBecado = Boolean(usuarioActual?.esBecado);
  const puedeUsarCafeteriaPorPerfil = Boolean(
    usuarioActual?.sistema === "Compras" || usuarioActual?.vip || esUsuarioBecado
  );
  const vouchersCalificantesCafeteria = useMemo(
    () =>
      (vouchersActivos || []).filter((voucher) =>
        isVoucherCalificanteCafeteria(voucher, {
          idSede: idSedeActual,
          fecha: dayjs(),
        })
      ),
    [vouchersActivos, idSedeActual]
  );
  const voucherAccesoCafeteria =
    vouchersCalificantesCafeteria.find(
      (voucher) => String(voucher.id) === String(idVoucherAccesoCafeteria)
    ) || null;
  const accesoCafeteriaSoloVoucher =
    !puedeUsarCafeteriaPorPerfil && vouchersCalificantesCafeteria.length > 0;
  const puedeUsarCafeteria =
    String(idSedeActual) === "1" &&
    (puedeUsarCafeteriaPorPerfil || accesoCafeteriaSoloVoucher);
  const { estado: bloqueoEvento, error: errorBloqueoEvento } = useBloqueoAutogestion({ token, idSede: idSedeActual, habilitado: open });
  const verificandoBloqueoCafeteria = open && !bloqueoEvento;
  const cafeteriaBloqueadaPorEvento = bloqueoEvento?.bloqueaCafeteria || bloqueoEvento?.bloqueaAppCompleta;
  const menuBloqueadoPorEvento = bloqueoEvento?.bloqueaMenu || bloqueoEvento?.bloqueaAppCompleta;
  const mensajeBloqueoCafeteria = bloqueoEvento?.mensaje || "Los pedidos de Cafetería no están disponibles durante este evento.";
  const mensajeBloqueoMenu = bloqueoEvento?.mensaje || "Los pedidos de Menú no están disponibles durante este evento.";
  const requiereVoucherCafeteria =
    selectedMenu === "cafeteria" && !puedeUsarCafeteriaPorPerfil;
  const requiereDestinoMenu = requiereDestinoExplicito(idSedeActual);

  useEffect(() => {
    if (!open || puedeUsarCafeteriaPorPerfil || String(idSedeActual) !== "1") {
      setIdVoucherAccesoCafeteria("");
      return;
    }

    if (vouchersCalificantesCafeteria.length === 1) {
      setIdVoucherAccesoCafeteria(String(vouchersCalificantesCafeteria[0].id));
      return;
    }

    const seleccionSigueDisponible = vouchersCalificantesCafeteria.some(
      (voucher) => String(voucher.id) === String(idVoucherAccesoCafeteria)
    );
    if (!seleccionSigueDisponible) {
      setIdVoucherAccesoCafeteria("");
      setCarrito([]);
    }
  }, [
    open,
    puedeUsarCafeteriaPorPerfil,
    idSedeActual,
    vouchersCalificantesCafeteria,
    idVoucherAccesoCafeteria,
  ]);

  useEffect(() => {
    if (
      selectedMenu === "cafeteria" &&
      (cafeteriaBloqueadaPorEvento ||
        (!loadingVouchersActivos && !puedeUsarCafeteria))
    ) {
      setSelectedMenu("");
      setIdVoucherAccesoCafeteria("");
      setCarrito([]);
      setStep(0);
    }
  }, [
    cafeteriaBloqueadaPorEvento,
    selectedMenu,
    loadingVouchersActivos,
    puedeUsarCafeteria,
  ]);

  useEffect(() => {
    if (requiereVoucherCafeteria && step >= 2 && !voucherAccesoCafeteria) {
      setCarrito([]);
      setStep(1);
    }
  }, [requiereVoucherCafeteria, step, voucherAccesoCafeteria]);

  useEffect(() => {
    const destinoPersistido = resolveDestinoForSede({
      idSede: idSedeActual,
      destino: localStorage.getItem("selectedLocation"),
    });

    if (requiereDestinoExplicito(idSedeActual)) {
      setSelectedLocation(destinoPersistido || "");
      if (destinoPersistido) {
        localStorage.setItem("selectedLocation", destinoPersistido);
      } else {
        localStorage.removeItem("selectedLocation");
      }
      return;
    }

    setSelectedLocation(DESTINO_VM);
    localStorage.removeItem("selectedLocation");
  }, [idSedeActual, open]);

  useEffect(() => {
    if (selectedMenu === "menu" && menuBloqueadoPorEvento) {
      setSelectedMenu(""); setCarrito([]); setStep(0);
    }
  }, [selectedMenu, menuBloqueadoPorEvento]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={{ ...style, width: { xs: "90%", sm: "90%", xxl: step >= 2 ? "70%" : "30%" } }}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">{`${step === 0
                  ? "Que tenes ganas de pedir?"
                  : step === 1
                    ? "Cuando y donde vas a comer?"
                    : step === 2
                      ? "Que vas a comer?"
                      : "Medio de pago"
                  }`}</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox pt={3}>
              <SoftBox
                display="flex"
                flexDirection={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="space-between"
                gap={2}
              >
                <SoftBox sx={{ width: "100%" }}>
                  <Collapse in={step === 0} unmountOnExit mountOnEnter>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SoftButton
                          color={
                            !selectedMenu
                              ? "uproYellow"
                              : selectedMenu === "menu"
                                ? "uproGreen"
                                : "secondary"
                          }
                          size="large"
                          circular
                          fullWidth
                          onClick={() => handleClick("menu")}
                          disabled={verificandoBloqueoCafeteria || menuBloqueadoPorEvento}
                        >
                          Menu diario&nbsp;
                          <AnimatedIcon animate={animatedIcon === "menu"}>restaurant</AnimatedIcon>
                        </SoftButton>
                      </Grid>
                      <Grid item xs={12}>
                        <SoftButton
                          color={
                            !selectedMenu
                              ? "uproYellow"
                              : selectedMenu === "cafeteria"
                                ? "uproGreen"
                                : "secondary"
                          }
                          size="large"
                          circular
                          fullWidth
                          onClick={() => handleClick("cafeteria")}
                          disabled={
                            verificandoBloqueoCafeteria ||
                            cafeteriaBloqueadaPorEvento ||
                            !puedeUsarCafeteria ||
                            (loadingVouchersActivos && !puedeUsarCafeteriaPorPerfil)
                          }
                        >
                          Cafeteria&nbsp;
                          <AnimatedIcon animate={animatedIcon === "cafeteria"}>
                            emoji_food_beverage
                          </AnimatedIcon>
                        </SoftButton>
                      </Grid>
                      {(menuBloqueadoPorEvento || errorBloqueoEvento) && <Grid item xs={12}><Alert severity="warning">{errorBloqueoEvento ? "No se pudo verificar la disponibilidad. Cierre y vuelva a abrir para reintentar." : mensajeBloqueoMenu}</Alert></Grid>}
                      {cafeteriaBloqueadaPorEvento && (
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            {mensajeBloqueoCafeteria}
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Collapse>
                  <Collapse in={step === 1 && selectedMenu === "menu"} unmountOnExit mountOnEnter>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Fecha del pedido
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <SoftInput
                            value={dayjs(fechaPedido).format("DD/MM/YYYY")}
                            disabled={true}
                          />
                          <Collapse in={usaDiaSiguiente && selectedMenu === "menu"}>
                            <SoftTypography variant="caption" color="info" fontWeight="bold">
                              Atencion: esta haciendo un pedido para consumir mañana en el turno {turnoSeleccionado?.nombre || " seleccionado"}!!
                            </SoftTypography>
                          </Collapse>
                        </SoftBox>
                      </Grid>
                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Turno
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <Select
                            value={turnoPedido || -1}
                            onChange={(event) => {
                              const turno = turnosDisponibles.find(
                                (item) => String(item.id) === String(event.target.value)
                              );
                              setHoraReserva("");
                              setFranjaSeleccionada(null);
                              setFranjasDisponibles([]);
                              setUsaDiaSiguiente(false);
                              setFechaPedido(dayjs());
                              setTurnoPedido(event.target.value);
                              setTurnoSeleccionado(turno || null);
                            }}
                            sx={{
                              " & .MuiSelect-select": {
                                padding: "0px !important",
                              },
                            }}
                          >
                            <MenuItem value={-1} disabled>
                              Seleccione un turno
                            </MenuItem>
                            {turnosDisponibles.map((turno) => (
                              <MenuItem
                                key={turno.id}
                                disabled={limiteDiarioAlcanzado}
                                value={turno.id}
                              >
                                {`${turno.nombre}${
                                  limiteDiarioAlcanzado
                                    ? ` (Limite diario ${limiteMaximoPedidos} alcanzado)`
                                    : ""
                                }`}
                              </MenuItem>
                            ))}
                          </Select>
                        </SoftBox>
                      </Grid>
                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Horario
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <Select
                            value={horaReserva || -1}
                            disabled={!turnoPedido}
                            onChange={(event) => {
                              const franja = franjasDisponibles.find(
                                (item) => String(item.id) === String(event.target.value)
                              );
                              setHoraReserva(event.target.value);
                              setFranjaSeleccionada(franja || null);
                            }}
                            sx={{
                              " & .MuiSelect-select": {
                                padding: "0px !important",
                              },
                            }}
                          >
                            <MenuItem value={-1} disabled>
                              {!turnoPedido
                                ? "Seleccione un turno primero"
                                : "Seleccione un horario"}
                            </MenuItem>
                            {franjasDisponibles.map((franja) => (
                              <MenuItem
                                key={franja.id}
                                value={franja.id}
                                disabled={!franja.disponible}
                              >
                                {`${franja?.horaInicio?.slice(0, -3)} - ${franja?.horaFin?.slice(
                                  0,
                                  -3
                                )} ${
                                  franja?.sinCupo
                                    ? "(Sin cupo)"
                                    : franja?.vencida
                                      ? "(Fuera de horario)"
                                      : franja?.fueraVentanaPedidos
                                        ? "(Fuera de ventana de pedidos)"
                                        : ""
                                }`}
                              </MenuItem>
                            ))}
                          </Select>
                        </SoftBox>
                      </Grid>
                      {localStorage.getItem("idSede") !== "1" && (
                        <Grid item xs={12}>
                          <SoftBox>
                            <InputLabel htmlFor="destino">
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                En que locacion vas a comer?
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                  &nbsp;*
                                </SoftTypography>
                              </SoftTypography>
                            </InputLabel>
                            <Select
                              value={selectedLocation}
                              onChange={(e) => {
                                const nextDestino = resolveDestinoForSede({
                                  idSede: idSedeActual,
                                  destino: e.target.value,
                                });
                                setSelectedLocation(nextDestino);
                                localStorage.setItem("selectedLocation", nextDestino);
                              }}
                              sx={{
                                " & .MuiSelect-select": {
                                  padding: "0px !important",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                Seleccione una locacion
                              </MenuItem>
                              {DESTINOS_SEDE_2.map((location) => (
                                <MenuItem key={location} value={location}>
                                  {location}
                                </MenuItem>
                              ))}
                            </Select>
                          </SoftBox>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              El pedido es para retirar?
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <Tabs
                            value={paraRetirar ? 1 : 0}
                            name="esElaborado"
                            onChange={(e, value) => {
                              setParaRetirar(value === 1);
                              setCarrito([]);
                            }}
                          >
                            <Tab
                              label={
                                <SoftTypography
                                  variant="h6"
                                  fontWeight={!paraRetirar ? "bold" : "regular"}
                                  color={!paraRetirar ? "primary" : ""}
                                >
                                  No
                                </SoftTypography>
                              }
                              value={0}
                            />
                            <Tab
                              label={
                                <SoftTypography
                                  variant="h6"
                                  fontWeight={paraRetirar ? "bold" : "regular"}
                                  color={paraRetirar ? "primary" : ""}
                                >
                                  Si
                                </SoftTypography>
                              }
                              value={1}
                            />
                          </Tabs>
                          {paraRetirar && (
                            <SoftBox mt={2}>
                              <Card>
                                <SoftBox
                                  p={1}
                                  bgColor="primary"
                                  justifyContent="center"
                                  display="flex"
                                  alignItems="center"
                                >
                                  <Icon
                                    sx={{
                                      color: "#fff",
                                    }}
                                  >
                                    info
                                  </Icon>
                                  &nbsp;
                                  <SoftTypography variant="h6" color="white">
                                    Debe llevar recipiente
                                  </SoftTypography>
                                </SoftBox>
                              </Card>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                  <Collapse
                    in={step === 1 && selectedMenu === "cafeteria"}
                    unmountOnExit
                    mountOnEnter
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Fecha del pedido
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <SoftInput
                            value={dayjs(fechaPedido).format("DD/MM/YYYY")}
                            disabled={true}
                          />
                        </SoftBox>
                      </Grid>
                      {requiereVoucherCafeteria && (
                        <Grid item xs={12}>
                          <SoftBox>
                            <InputLabel htmlFor="voucherAccesoCafeteria">
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Voucher para Cafetería
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                  &nbsp;*
                                </SoftTypography>
                              </SoftTypography>
                            </InputLabel>
                            <Select
                              id="voucherAccesoCafeteria"
                              value={idVoucherAccesoCafeteria || ""}
                              disabled={
                                loadingVouchersActivos || vouchersCalificantesCafeteria.length === 1
                              }
                              onChange={(event) => {
                                setIdVoucherAccesoCafeteria(String(event.target.value));
                                setCarrito([]);
                              }}
                              fullWidth
                              sx={{ "& .MuiSelect-select": { padding: "0px !important" } }}
                            >
                              <MenuItem value="" disabled>
                                Seleccioná un voucher
                              </MenuItem>
                              {vouchersCalificantesCafeteria.map((voucher) => {
                                const destino = getVoucherTarget(voucher);
                                return (
                                  <MenuItem key={voucher.id} value={String(voucher.id)}>
                                    {`${voucher.nombreVoucher || voucher.grupo?.nombre || "Voucher"} · ${getVoucherTypeLabel(voucher.tipo)}${destino ? `: ${destino}` : ""} · Disponible ${formatVoucherValue(Math.min(Number(voucher.saldo || 0), Number(voucher.saldoDiario || 0)), voucher.tipo)}`}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <SoftTypography variant="caption" color="secondary" display="block" mt={0.75}>
                              El catálogo y las cantidades se ajustarán al voucher elegido, que se aplicará como medio de pago.
                            </SoftTypography>
                          </SoftBox>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <SoftBox>
                          <InputLabel htmlFor="idConvenio">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                              Necesitas que te llevemos el pedido?
                              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                              </SoftTypography>
                            </SoftTypography>
                          </InputLabel>
                          <Tabs
                            value={paraLlevar ? 1 : 0}
                            name="esElaborado"
                            onChange={(e, value) => {
                              setParaLlevar(value === 1);
                              setCarrito([]);
                            }}
                          >
                            <Tab
                              label={
                                <SoftTypography
                                  variant="h6"
                                  fontWeight={!paraLlevar ? "bold" : "regular"}
                                  color={!paraLlevar ? "primary" : ""}
                                >
                                  No
                                </SoftTypography>
                              }
                              value={0}
                            />
                            <Tab
                              label={
                                <SoftTypography
                                  variant="h6"
                                  fontWeight={paraLlevar ? "bold" : "regular"}
                                  color={paraLlevar ? "primary" : ""}
                                >
                                  Si
                                </SoftTypography>
                              }
                              value={1}
                            />
                          </Tabs>
                        </SoftBox>
                      </Grid>
                      {paraLlevar && (
                        <Grid item xs={12}>
                          <SoftBox>
                            <InputLabel htmlFor="dondeLlevar">
                              <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Donde te lo llevamos?
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                  &nbsp;*
                                </SoftTypography>
                              </SoftTypography>
                            </InputLabel>
                            <SoftInput
                              value={dondeLlevar}
                              onChange={(e) => setDondeLlevar(e.target.value)}
                              placeholder="Ej: Salon 1, Oficina de administracion, etc."
                              id="dondeLlevar"
                              name="dondeLlevar"
                            />
                          </SoftBox>
                        </Grid>
                      )}
                    </Grid>
                  </Collapse>
                  <Collapse in={step === 2 && selectedMenu === "menu"} unmountOnExit mountOnEnter>
                    <MenuList
                      fechaPedido={fechaPedido}
                      token={token}
                      carrito={carrito}
                      setCarrito={setCarrito}
                      turnoPedido={turnoSeleccionadoNombre}
                      usuario={usuarioActual}
                      destino={selectedLocation}
                    />
                  </Collapse>
                  <Collapse
                    in={step === 2 && selectedMenu === "cafeteria"}
                    unmountOnExit
                    mountOnEnter
                  >
                    <Cafeteria
                      fechaPedido={fechaPedido}
                      token={token}
                      idCliente={usuarioSincronizado?.cliente || usuario?.cliente}
                      carrito={carrito}
                      setCarrito={setCarrito}
                      voucherRestriccion={
                        requiereVoucherCafeteria ? voucherAccesoCafeteria : null
                      }
                    />
                  </Collapse>
                  <Collapse in={step === 3} unmountOnExit mountOnEnter>
                    <SoftBox display="flex" flexDirection="column" gap={2}>
                      <Collapse in={!mostrarVouchers} mountOnEnter unmountOnExit>
                        <SoftBox display="flex" flexDirection="column" gap={2}>
                          <SoftTypography variant="button" color="secondary">
                            Elegí cómo querés pagar tu pedido.
                          </SoftTypography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <SoftButton
                                color="primary"
                                circular
                                fullWidth
                                onClick={() => setMostrarVouchers(true)}
                              >
                                <Icon>redeem</Icon>&nbsp;Usar voucher/beneficio
                              </SoftButton>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <SoftButton
                                color="uproYellow"
                                circular
                                fullWidth
                                onClick={() => {
                                  setVoucherSeleccionado(null);
                                  setOpenModalConfirmar(true);
                                }}
                              >
                                <Icon>payments</Icon>&nbsp;Usar otro medio
                              </SoftButton>
                            </Grid>
                          </Grid>
                        </SoftBox>
                      </Collapse>
                      <Collapse in={mostrarVouchers} mountOnEnter unmountOnExit>
                        <SoftBox display="flex" flexDirection="column" gap={1.5} aria-live="polite">
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={1.25}
                          >
                            <SoftBox display="flex" alignItems="center" gap={1.25}>
                              <SoftBox
                                width={40}
                                height={40}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="12px"
                                sx={{ background: "#e8fbf8", color: "#079a9d" }}
                              >
                                <Icon>redeem</Icon>
                              </SoftBox>
                              <SoftTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#102a5b", lineHeight: 1.2 }}
                              >
                                Vouchers y beneficios
                              </SoftTypography>
                            </SoftBox>
                            <SoftButton
                              size="small"
                              circular
                              onClick={toggleTodosVouchers}
                              aria-label={todosVouchersExpandidos ? "Contraer todos los vouchers" : "Expandir todos los vouchers"}
                              sx={{
                                minHeight: "34px",
                                px: 1.5,
                                color: "#102a5b",
                                background: "#ffffff",
                                border: "1px solid #e4eaf0",
                                boxShadow: "0 3px 10px rgba(15, 42, 91, 0.08)",
                                "&:hover": {
                                  background: "#f7fafc",
                                  borderColor: "#cfd9e3",
                                },
                                "&:focus-visible": {
                                  outline: "3px solid rgba(7, 154, 157, 0.3)",
                                  outlineOffset: "2px",
                                },
                              }}
                            >
                              <Icon>{todosVouchersExpandidos ? "unfold_less" : "unfold_more"}</Icon>&nbsp;
                              {todosVouchersExpandidos ? "Contraer todos" : "Expandir todos"}
                            </SoftButton>
                          </SoftBox>
                          {vouchersPedido.map((voucher) => {
                            const destinoVoucher = getVoucherTarget(voucher);
                            const estaExpandido = vouchersExpandidos.includes(voucher.id);
                            const detallesVoucherId = `voucher-detalle-${voucher.id}`;
                            const metricasVoucher = [
                              {
                                icono: "inventory_2",
                                etiqueta: "Disponible",
                                valor: formatVoucherValue(voucher.saldo, voucher.tipo),
                              },
                              voucher.limiteDiario
                                ? {
                                    icono: "event",
                                    etiqueta: "Límite diario",
                                    valor: formatVoucherValue(voucher.limiteDiario, voucher.tipo),
                                  }
                                : null,
                              voucher.limiteDiario
                                ? {
                                    icono: "trending_up",
                                    etiqueta: "Consumido hoy",
                                    valor: formatVoucherValue(voucher.consumidoHoy, voucher.tipo),
                                  }
                                : null,
                            ].filter(Boolean);
                            return (
                              <Card
                                key={voucher.id}
                                sx={{
                                  border: voucher.elegible
                                    ? "1px solid #e3ebe9"
                                    : "1px solid rgba(211, 47, 47, 0.28)",
                                  borderRadius: "16px",
                                  background: voucher.elegible ? "#ffffff" : "#fffafb",
                                  boxShadow: "0 6px 18px rgba(15, 42, 91, 0.06)",
                                  overflow: "hidden",
                                }}
                              >
                                <SoftBox p={{ xs: 1.5, sm: 2 }}>
                                  <SoftBox
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={estaExpandido}
                                    aria-controls={detallesVoucherId}
                                    onClick={() => toggleVoucherExpandido(voucher.id)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleVoucherExpandido(voucher.id);
                                      }
                                    }}
                                    sx={{
                                      cursor: "pointer",
                                      borderRadius: "12px",
                                      transition: "background-color 180ms ease",
                                      "&:hover": { background: "rgba(7, 154, 157, 0.035)" },
                                      "&:focus-visible": {
                                        outline: "3px solid rgba(7, 154, 157, 0.35)",
                                        outlineOffset: "3px",
                                      },
                                      "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                      },
                                    }}
                                  >
                                    <SoftBox
                                      display="inline-flex"
                                      alignItems="center"
                                      gap={0.5}
                                      px={1}
                                      py={0.4}
                                      mb={1.25}
                                      borderRadius="999px"
                                      sx={{
                                        background: voucher.elegible ? "#e8f8eb" : "#fff0f1",
                                        color: voucher.elegible ? "#278c35" : "#c62828",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: "0.95rem !important" }}>
                                        {voucher.elegible ? "check_circle" : "error_outline"}
                                      </Icon>
                                      <SoftTypography variant="caption" fontWeight="bold">
                                        {voucher.elegible ? "Aplicable" : "No aplicable"}
                                      </SoftTypography>
                                    </SoftBox>
                                    <SoftBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="flex-start"
                                      gap={1.5}
                                    >
                                      <SoftBox minWidth={0}>
                                        <SoftTypography
                                          variant="button"
                                          fontWeight="bold"
                                          display="block"
                                          sx={{ color: "#102a5b", lineHeight: 1.35 }}
                                        >
                                          {voucher.nombreVoucher || voucher.grupo?.nombre || "Voucher"}
                                        </SoftTypography>
                                        <SoftTypography
                                          variant="caption"
                                          display="block"
                                          mt={0.5}
                                          sx={{ color: "#65758b" }}
                                        >
                                          {getVoucherTypeLabel(voucher.tipo)}
                                          {destinoVoucher ? `: ${destinoVoucher}` : ""}
                                        </SoftTypography>
                                        <SoftBox display="flex" alignItems="center" gap={0.5} mt={0.35}>
                                          <Icon sx={{ color: "#079a9d", fontSize: "1rem !important" }}>
                                            location_on
                                          </Icon>
                                          <SoftTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "#079a9d" }}
                                          >
                                            {voucher?.grupo?.sede?.nombre
                                              ? `Canjeable en ${voucher.grupo.sede.nombre}`
                                              : "Canjeable en todas las sedes"}
                                          </SoftTypography>
                                        </SoftBox>
                                      </SoftBox>
                                      <Icon
                                        aria-hidden="true"
                                        sx={{ color: "#36547c", mt: 0.25, flexShrink: 0 }}
                                      >
                                        {estaExpandido ? "expand_less" : "expand_more"}
                                      </Icon>
                                    </SoftBox>
                                  </SoftBox>

                                  <SoftBox
                                    mt={1.5}
                                    p={1.25}
                                    sx={{
                                      borderRadius: "10px",
                                      border: voucher.elegible
                                        ? "1px solid #bfe4c7"
                                        : "1px solid #f1c3c3",
                                      borderLeft: voucher.elegible
                                        ? "4px solid #25a93b"
                                        : "4px solid #d32f2f",
                                      background: voucher.elegible
                                        ? "linear-gradient(100deg, #f1fbf3 0%, #f7fcf8 100%)"
                                        : "#fff5f5",
                                    }}
                                  >
                                    <SoftBox display="flex" alignItems="center" gap={0.75}>
                                      <Icon
                                        sx={{
                                          color: voucher.elegible ? "#279c32" : "#d32f2f",
                                          fontSize: "1.15rem !important",
                                        }}
                                      >
                                        {voucher.elegible ? "check_circle" : "error_outline"}
                                      </Icon>
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: voucher.elegible ? "#278c35" : "#c62828" }}
                                      >
                                        El voucher cubre ${formatMoneyPunto(voucher.montoDescuento || 0)}
                                      </SoftTypography>
                                    </SoftBox>
                                    <SoftBox display="flex" alignItems="center" gap={0.75} mt={0.75}>
                                      <Icon sx={{ color: "#36547c", fontSize: "1.1rem !important" }}>
                                        credit_card
                                      </Icon>
                                      <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#102a5b" }}>
                                        Restante a pagar: ${formatMoneyPunto(voucher.montoRestante || 0)}
                                      </SoftTypography>
                                    </SoftBox>
                                  </SoftBox>

                                  <Collapse in={estaExpandido} id={detallesVoucherId} mountOnEnter unmountOnExit>
                                    <SoftBox
                                      mt={1.25}
                                      sx={{
                                        border: "1px solid #e4eaf0",
                                        borderRadius: "11px",
                                        overflow: "hidden",
                                        background: "#ffffff",
                                      }}
                                    >
                                      {metricasVoucher.map((metrica, index) => (
                                        <SoftBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1.25}
                                          px={1.25}
                                          py={1.1}
                                          key={metrica.etiqueta}
                                          sx={{
                                            borderBottom:
                                              index < metricasVoucher.length - 1
                                                ? "1px solid #edf1f5"
                                                : "none",
                                          }}
                                        >
                                          <SoftBox
                                            width={34}
                                            height={34}
                                            minWidth={34}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            borderRadius="10px"
                                            sx={{ background: "#edf8f7", color: "#079a9d" }}
                                          >
                                            <Icon sx={{ fontSize: "1.05rem !important" }}>
                                              {metrica.icono}
                                            </Icon>
                                          </SoftBox>
                                          <SoftBox>
                                            <SoftTypography
                                              variant="caption"
                                              display="block"
                                              sx={{ color: "#7a8799", lineHeight: 1.25 }}
                                            >
                                              {metrica.etiqueta}
                                            </SoftTypography>
                                            <SoftTypography
                                              variant="caption"
                                              fontWeight="bold"
                                              display="block"
                                              sx={{ color: "#102a5b", mt: 0.25 }}
                                            >
                                              {metrica.valor}
                                            </SoftTypography>
                                          </SoftBox>
                                        </SoftBox>
                                      ))}
                                    </SoftBox>

                                    <SoftBox
                                      mt={1.25}
                                      p={1.25}
                                      sx={{
                                        borderRadius: "10px",
                                        border: "1px solid #d9e9fb",
                                        background: "linear-gradient(110deg, #f4f9ff 0%, #eef6ff 100%)",
                                      }}
                                    >
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        display="block"
                                        sx={{ color: "#36547c" }}
                                      >
                                        Cantidad aplicable
                                      </SoftTypography>
                                      <SoftBox display="flex" alignItems="center" gap={0.75} mt={0.75}>
                                        <SoftBox
                                          minWidth={30}
                                          height={30}
                                          px={0.75}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          borderRadius="999px"
                                          sx={{ background: "#e4f0fc", color: "#36547c" }}
                                        >
                                          <SoftTypography variant="caption" fontWeight="bold">
                                            {voucher.tipo === "MONTO"
                                              ? formatVoucherValue(voucher.cantidadAplicable, voucher.tipo)
                                              : Number(voucher.cantidadAplicable || 0).toLocaleString("es-AR")}
                                          </SoftTypography>
                                        </SoftBox>
                                        {voucher.tipo !== "MONTO" ? (
                                          <SoftTypography variant="caption" sx={{ color: "#65758b" }}>
                                            de {formatVoucherValue(voucher.unidadesElegibles, voucher.tipo)}
                                          </SoftTypography>
                                        ) : null}
                                      </SoftBox>
                                      <SoftBox mt={1} pt={1} sx={{ borderTop: "1px solid #d8e3ef" }}>
                                        <SoftTypography
                                          variant="caption"
                                          fontWeight="bold"
                                          display="block"
                                          sx={{ color: "#279c32" }}
                                        >
                                          El voucher cubre ${formatMoneyPunto(voucher.montoDescuento || 0)}
                                        </SoftTypography>
                                        <SoftTypography
                                          variant="caption"
                                          fontWeight="bold"
                                          display="block"
                                          mt={0.5}
                                          sx={{ color: "#102a5b" }}
                                        >
                                          Restante a pagar: ${formatMoneyPunto(voucher.montoRestante || 0)}
                                        </SoftTypography>
                                      </SoftBox>
                                    </SoftBox>

                                    {!voucher.elegible && voucher.motivoBloqueo ? (
                                      <SoftBox
                                        display="flex"
                                        alignItems="flex-start"
                                        gap={0.75}
                                        mt={1}
                                        role="alert"
                                      >
                                        <Icon color="error" sx={{ fontSize: "1rem !important", mt: 0.15 }}>
                                          info
                                        </Icon>
                                        <SoftTypography variant="caption" color="error">
                                          {voucher.motivoBloqueo}
                                        </SoftTypography>
                                      </SoftBox>
                                    ) : null}

                                    <SoftButton
                                      fullWidth
                                      circular
                                      color="uproGreen"
                                      disabled={!voucher.elegible}
                                      onClick={() => {
                                        setVoucherSeleccionado(voucher);
                                        setOpenModalConfirmar(true);
                                      }}
                                      sx={{ mt: 1.5, minHeight: "42px" }}
                                    >
                                      <Icon>done</Icon>&nbsp;Aplicar
                                    </SoftButton>
                                  </Collapse>
                                </SoftBox>
                              </Card>
                            );
                          })}
                        </SoftBox>
                      </Collapse>
                    </SoftBox>
                  </Collapse>
                  <SoftBox
                    mt={2}
                    display="flex"
                    justifyContent={step > 0 ? "space-between" : "flex-end"}
                    gap={2}
                  >
                    <Collapse in={step > 0} unmountOnExit mountOnEnter>
                      <SoftButton
                        color="primary"
                        circular
                        onClick={() => {
                          if (step === 3 && mostrarVouchers) {
                            setMostrarVouchers(false);
                            setVoucherSeleccionado(null);
                          } else if (step === 3) {
                            setStep(2);
                            setVoucherSeleccionado(null);
                          } else {
                            setStep(step - 1);
                            setCarrito([]);
                          }
                        }}
                        fullWidth
                      >
                        <Icon>arrow_back</Icon>&nbsp;Volver
                      </SoftButton>
                    </Collapse>
                    <Collapse in={step < 3} unmountOnExit mountOnEnter>
                      <SoftButton
                        color="uproGreen"
                        circular
                        onClick={() => {
                          if (step === 2) {
                            handleFinalizarPedido();
                          } else {
                            setStep(step + 1);
                            setCarrito([]);
                          }
                        }}
                        disabled={
                          (step === 0 && !selectedMenu) ||
                          (step === 1 &&
                            selectedMenu === "menu" &&
                            (!turnoPedido || !horaReserva || (requiereDestinoMenu && !selectedLocation))) ||
                          (step === 1 &&
                            selectedMenu === "cafeteria" &&
                            ((paraLlevar && !dondeLlevar) ||
                              (requiereVoucherCafeteria && !voucherAccesoCafeteria))) ||
                          (step === 2 &&
                            selectedMenu === "cafeteria" &&
                            carrito?.filter((item) => !item.eliminado)?.length === 0) ||
                          (step === 2 &&
                            selectedMenu === "menu" &&
                            ((carrito?.filter((item) => !item.eliminado)?.length !== 1 &&
                              !usuarioActual?.vip) ||
                              carrito?.filter((item) => !item.eliminado)?.length === 0)) ||
                          deshabilitar ||
                          syncingCliente ||
                          loadingVouchersPedido ||
                          !clienteSyncReady
                        }
                        fullWidth
                      >
                        {step === 2 && loadingVouchersPedido
                          ? "Buscando beneficios..."
                          : step === 2
                            ? "Finalizar pedido"
                            : "Siguiente"}
                        &nbsp;
                        {syncingCliente || loadingVouchersPedido ? (
                          <CircularProgress size={15} color="inherit" />
                        ) : (
                          <Icon>arrow_forward</Icon>
                        )}
                      </SoftButton>
                    </Collapse>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <ModalConfirmarPedido
              open={openModalConfirmar}
              handleClose={() => setOpenModalConfirmar(false)}
              handleCloseAll={() => {
                setOpenModalConfirmar(false);
                handleClose();
                if (typeof onPedidoCreado !== "function" && typeof refetch === "function") {
                  refetch();
                }
              }}
              onPedidoCreado={onPedidoCreado}
              carrito={carrito?.filter((item) => !item.eliminado)}
              tipo={selectedMenu}
              fechaPedido={fechaPedido}
              menusDelDia={menusDelDia?.menusDelDia}
              horaReserva={franjaSeleccionada?.horaInicio}
              turnoPedido={turnoSeleccionadoNombre || null}
              idTurnoSede={turnoSeleccionado?.id || null}
              idFranjaTurno={franjaSeleccionada?.id || null}
              paraRetirar={paraRetirar}
              dondeLlevar={paraLlevar ? `${dondeLlevar}` : ""}
              usuario={usuarioActual}
              token={token}
              destino={selectedLocation}
              idSede={idSedeActual}
              saldoCuentaCorrientePendienteActual={saldoCuentaCorrientePendienteActual}
              limiteCuentaCorriente={limiteCuentaCorriente}
              bloqueoCuentaCorrienteLimite={bloqueoCuentaCorrienteLimite}
              voucherSeleccionado={voucherSeleccionado}
              onCondicionesPagoCambiaron={refetch}
              onVolverMedioPago={
                !requiereVoucherCafeteria && vouchersPedido.length > 0
                  ? () => setOpenModalConfirmar(false)
                  : undefined
              }
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevoPedido.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  onPedidoCreado: PropTypes.func,
  pedidosHoy: PropTypes.array,
  token: PropTypes.string,
  usuario: PropTypes.object,
  vouchersActivos: PropTypes.array,
  loadingVouchersActivos: PropTypes.bool,
  onActualizarVouchers: PropTypes.func,
  deshabilitar: PropTypes.bool,
  saldoCuentaCorrientePendienteActual: PropTypes.number,
  limiteCuentaCorriente: PropTypes.number,
  bloqueoCuentaCorrienteLimite: PropTypes.bool,
};

const PREVISUALIZAR_VOUCHERS_PEDIDO = gql`
  query previsualizarVouchersPedidoNuevoPedido(
    $idCliente: Int
    $dni: String
    $idUsuario: Int
    $appUsuario: String
    $fechaOperacion: Date
    $idSede: Int
    $totalPedido: Float
    $detalles: [PedidoDetalleInput]
    $token: String
  ) {
    previsualizarVouchersPedido(
      idCliente: $idCliente
      dni: $dni
      idUsuario: $idUsuario
      appUsuario: $appUsuario
      fechaOperacion: $fechaOperacion
      idSede: $idSede
      totalPedido: $totalPedido
      detalles: $detalles
      token: $token
    ) {
      id
      nombreVoucher
      desde
      hasta
      tipo
      idProducto
      idCategoria
      cantidad
      limiteDiario
      habilitado
      consumido
      consumidoHoy
      saldo
      saldoDiario
      cantidadAplicable
      unidadesElegibles
      montoDescuento
      montoRestante
      requierePagoComplementario
      elegible
      motivoBloqueo
      categoria { id categoria }
      producto { id nombre categoriaProducto { id categoria } }
      grupo { id nombre estado idSede sede { id nombre } }
    }
  }
`;

const formatVoucherValue = (value, tipo) =>
  tipo === "MONTO"
    ? `$ ${formatMoneyPunto(value || 0)}`
    : `${Number(value || 0).toLocaleString("es-AR")} unidades`;

const getVoucherTypeLabel = (tipo) =>
  tipo === "MONTO" ? "Monto" : tipo === "PRODUCTO" ? "Producto" : "Categoría";

const getVoucherTarget = (voucher) =>
  voucher?.tipo === "PRODUCTO"
    ? voucher?.producto?.nombre
    : voucher?.tipo === "CATEGORIA"
      ? voucher?.categoria?.categoria
      : null;

